// QRISE Mystery Box — 5 SVG presets used by both the editor preview and
// the live game animation. Each style returns a {body, lid} pair so the
// canvas/SVG layer can rotate the lid open without touching the body.
//
// Designed at viewBox 200×200, transparent background. The lid pivots
// around the back hinge ((100, 88) for square boxes; (100, 92) for the
// wooden chest). Frontend animation handles the transform-origin.

const SIZE = 200;
const svgWrap = (inner) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${SIZE} ${SIZE}" width="100%" height="100%">${inner}</svg>`;
const dataUri = (svg) => `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;

const STYLES = {
  // --- 1. Gold gift box ----------------------------------------------------
  gold_gift: {
    label: "Boîte cadeau dorée",
    labelEn: "Gold gift box",
    accentColor: "#f59e0b",
    bodyHinge: 88,
    body: svgWrap(`
      <defs>
        <linearGradient id="ggb_box" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#fde68a"/>
          <stop offset="35%" stop-color="#f59e0b"/>
          <stop offset="100%" stop-color="#7c2d12"/>
        </linearGradient>
        <linearGradient id="ggb_gloss" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="rgba(255,255,255,0.55)"/>
          <stop offset="40%" stop-color="rgba(255,255,255,0)"/>
        </linearGradient>
        <linearGradient id="ggb_ribv" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#fff7d6"/>
          <stop offset="50%" stop-color="#fde68a"/>
          <stop offset="100%" stop-color="#b45309"/>
        </linearGradient>
        <linearGradient id="ggb_ribh" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stop-color="#fcd34d"/>
          <stop offset="50%" stop-color="#fff7d6"/>
          <stop offset="100%" stop-color="#fcd34d"/>
        </linearGradient>
      </defs>
      <ellipse cx="100" cy="186" rx="74" ry="9" fill="rgba(0,0,0,0.32)"/>
      <rect x="36" y="88" width="128" height="92" rx="6" fill="url(#ggb_box)"/>
      <rect x="36" y="88" width="128" height="46" rx="6" fill="url(#ggb_gloss)"/>
      <rect x="36" y="88" width="128" height="92" rx="6" fill="none" stroke="rgba(0,0,0,0.18)" stroke-width="1"/>
      <rect x="92" y="88" width="16" height="92" fill="url(#ggb_ribv)"/>
      <rect x="36" y="125" width="128" height="14" fill="url(#ggb_ribh)"/>
      <line x1="36" y1="126" x2="164" y2="126" stroke="rgba(0,0,0,0.14)"/>
      <line x1="36" y1="139" x2="164" y2="139" stroke="rgba(0,0,0,0.14)"/>
      <line x1="92" y1="88" x2="92" y2="180" stroke="rgba(0,0,0,0.14)"/>
      <line x1="108" y1="88" x2="108" y2="180" stroke="rgba(0,0,0,0.14)"/>
      <path d="M36 88 L164 88 L156 100 L44 100 Z" fill="rgba(0,0,0,0.22)"/>
    `),
    lid: svgWrap(`
      <defs>
        <linearGradient id="ggb_lid" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#fff5b8"/>
          <stop offset="40%" stop-color="#fbbf24"/>
          <stop offset="100%" stop-color="#92400e"/>
        </linearGradient>
        <linearGradient id="ggb_lidshine" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="rgba(255,255,255,0.7)"/>
          <stop offset="60%" stop-color="rgba(255,255,255,0)"/>
        </linearGradient>
        <radialGradient id="ggb_bow" cx="50%" cy="40%" r="60%">
          <stop offset="0%" stop-color="#fff5b8"/>
          <stop offset="60%" stop-color="#fbbf24"/>
          <stop offset="100%" stop-color="#92400e"/>
        </radialGradient>
      </defs>
      <rect x="32" y="62" width="136" height="36" rx="4" fill="url(#ggb_lid)"/>
      <rect x="32" y="62" width="136" height="14" fill="url(#ggb_lidshine)"/>
      <rect x="32" y="62" width="136" height="6" fill="#fff5b8"/>
      <rect x="32" y="62" width="136" height="36" rx="4" fill="none" stroke="rgba(0,0,0,0.18)" stroke-width="1"/>
      <rect x="92" y="62" width="16" height="36" fill="url(#ggb_ribv)"/>
      <line x1="92" y1="62" x2="92" y2="98" stroke="rgba(0,0,0,0.16)"/>
      <line x1="108" y1="62" x2="108" y2="98" stroke="rgba(0,0,0,0.16)"/>
      <path d="M62 62 Q60 30 84 28 Q100 32 100 56 Q100 32 116 28 Q140 30 138 62 Z" fill="url(#ggb_bow)" stroke="#7c2d12" stroke-width="1"/>
      <ellipse cx="78" cy="42" rx="14" ry="18" fill="url(#ggb_bow)" stroke="#7c2d12" stroke-width="1"/>
      <ellipse cx="122" cy="42" rx="14" ry="18" fill="url(#ggb_bow)" stroke="#7c2d12" stroke-width="1"/>
      <ellipse cx="78" cy="38" rx="6" ry="8" fill="rgba(255,255,255,0.55)"/>
      <ellipse cx="122" cy="38" rx="6" ry="8" fill="rgba(255,255,255,0.55)"/>
      <circle cx="100" cy="58" r="7" fill="#7c2d12"/>
      <circle cx="98" cy="56" r="2.4" fill="rgba(255,255,255,0.7)"/>
    `),
  },

  // --- 2. Wooden treasure chest -------------------------------------------
  wooden_chest: {
    label: "Coffre au trésor",
    labelEn: "Treasure chest",
    accentColor: "#f59e0b",
    bodyHinge: 92,
    body: svgWrap(`
      <defs>
        <linearGradient id="wc_body" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#b45309"/>
          <stop offset="55%" stop-color="#7c2d12"/>
          <stop offset="100%" stop-color="#3f1d0a"/>
        </linearGradient>
        <linearGradient id="wc_gloss" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="rgba(255,220,180,0.35)"/>
          <stop offset="60%" stop-color="rgba(255,255,255,0)"/>
        </linearGradient>
        <linearGradient id="wc_brass" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#fde68a"/>
          <stop offset="55%" stop-color="#f59e0b"/>
          <stop offset="100%" stop-color="#7c2d12"/>
        </linearGradient>
      </defs>
      <ellipse cx="100" cy="188" rx="78" ry="9" fill="rgba(0,0,0,0.4)"/>
      <rect x="28" y="92" width="144" height="90" rx="3" fill="url(#wc_body)"/>
      <rect x="28" y="92" width="144" height="32" fill="url(#wc_gloss)"/>
      <line x1="60" y1="92" x2="60" y2="182" stroke="rgba(0,0,0,0.45)" stroke-width="1.5"/>
      <line x1="100" y1="92" x2="100" y2="182" stroke="rgba(0,0,0,0.45)" stroke-width="1.5"/>
      <line x1="140" y1="92" x2="140" y2="182" stroke="rgba(0,0,0,0.45)" stroke-width="1.5"/>
      <rect x="28" y="118" width="144" height="6" fill="url(#wc_brass)"/>
      <rect x="28" y="158" width="144" height="6" fill="url(#wc_brass)"/>
      <line x1="28" y1="121" x2="172" y2="121" stroke="rgba(0,0,0,0.3)"/>
      <line x1="28" y1="161" x2="172" y2="161" stroke="rgba(0,0,0,0.3)"/>
      <rect x="92" y="135" width="16" height="22" rx="2" fill="url(#wc_brass)" stroke="#3f1d0a" stroke-width="0.6"/>
      <circle cx="100" cy="146" r="3.2" fill="#1c0a04"/>
      <rect x="28" y="92" width="6" height="90" fill="url(#wc_brass)"/>
      <rect x="166" y="92" width="6" height="90" fill="url(#wc_brass)"/>
      <line x1="34" y1="92" x2="34" y2="182" stroke="rgba(0,0,0,0.35)"/>
      <line x1="166" y1="92" x2="166" y2="182" stroke="rgba(0,0,0,0.35)"/>
      <circle cx="40" cy="100" r="2" fill="#7c2d12"/>
      <circle cx="40" cy="174" r="2" fill="#7c2d12"/>
      <circle cx="160" cy="100" r="2" fill="#7c2d12"/>
      <circle cx="160" cy="174" r="2" fill="#7c2d12"/>
    `),
    lid: svgWrap(`
      <defs>
        <linearGradient id="wc_lid" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#d97706"/>
          <stop offset="55%" stop-color="#92400e"/>
          <stop offset="100%" stop-color="#451a03"/>
        </linearGradient>
        <linearGradient id="wc_lidshine" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="rgba(255,220,180,0.55)"/>
          <stop offset="55%" stop-color="rgba(255,220,180,0)"/>
        </linearGradient>
        <linearGradient id="wc_lidbrass" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#fde68a"/>
          <stop offset="55%" stop-color="#f59e0b"/>
          <stop offset="100%" stop-color="#7c2d12"/>
        </linearGradient>
      </defs>
      <path d="M28 92 Q100 28 172 92 Z" fill="url(#wc_lid)"/>
      <path d="M28 92 Q100 28 172 92" fill="url(#wc_lidshine)"/>
      <path d="M40 90 Q100 42 160 90" stroke="rgba(0,0,0,0.55)" stroke-width="1.5" opacity="0.6" fill="none"/>
      <path d="M52 92 Q100 56 148 92" stroke="rgba(0,0,0,0.55)" stroke-width="1.2" opacity="0.5" fill="none"/>
      <path d="M64 92 Q100 68 136 92" stroke="rgba(0,0,0,0.55)" stroke-width="1" opacity="0.45" fill="none"/>
      <rect x="92" y="80" width="16" height="14" rx="2" fill="url(#wc_lidbrass)" stroke="#3f1d0a" stroke-width="0.6"/>
      <rect x="40" y="84" width="14" height="12" rx="2" fill="url(#wc_lidbrass)" stroke="#3f1d0a" stroke-width="0.5"/>
      <rect x="146" y="84" width="14" height="12" rx="2" fill="url(#wc_lidbrass)" stroke="#3f1d0a" stroke-width="0.5"/>
      <circle cx="100" cy="86" r="1.5" fill="#3f1d0a"/>
    `),
  },

  // --- 3. Premium card pack ------------------------------------------------
  card_pack: {
    label: "Pack de cartes premium",
    labelEn: "Premium card pack",
    accentColor: "#0ea5e9",
    bodyHinge: 50,
    body: svgWrap(`
      <defs>
        <linearGradient id="cp_body" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#1e3a8a"/>
          <stop offset="50%" stop-color="#0ea5e9"/>
          <stop offset="100%" stop-color="#0c4a6e"/>
        </linearGradient>
        <linearGradient id="cp_shine" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="rgba(255,255,255,0.45)"/>
          <stop offset="40%" stop-color="rgba(255,255,255,0)"/>
        </linearGradient>
      </defs>
      <ellipse cx="100" cy="188" rx="50" ry="8" fill="rgba(0,0,0,0.34)"/>
      <rect x="58" y="50" width="84" height="132" rx="6" fill="url(#cp_body)"/>
      <rect x="58" y="50" width="84" height="132" rx="6" fill="url(#cp_shine)"/>
      <text x="100" y="118" font-family="serif" font-size="24" font-weight="900" fill="#fde68a" text-anchor="middle" font-style="italic">QRISE</text>
      <text x="100" y="138" font-family="sans-serif" font-size="9" font-weight="700" fill="#e0f2fe" text-anchor="middle" letter-spacing="3">PREMIUM PACK</text>
      <path d="M58 108 L142 108" stroke="rgba(255,255,255,0.18)" stroke-width="0.8"/>
      <path d="M58 158 L142 158" stroke="rgba(255,255,255,0.18)" stroke-width="0.8"/>
      <circle cx="100" cy="172" r="4" fill="#fde68a"/>
    `),
    lid: svgWrap(`
      <defs>
        <linearGradient id="cp_lid" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#fde047"/><stop offset="100%" stop-color="#d97706"/>
        </linearGradient>
      </defs>
      <path d="M58 50 L142 50 L138 70 L62 70 Z" fill="url(#cp_lid)"/>
      <path d="M58 50 L142 50 L138 70 L62 70 Z" fill="rgba(255,255,255,0.25)"/>
      <text x="100" y="65" font-family="sans-serif" font-size="8" font-weight="900" fill="#451a03" text-anchor="middle" letter-spacing="3">PULL TO OPEN</text>
    `),
  },

  // --- 4. Dark magical box -------------------------------------------------
  dark_magic: {
    label: "Boîte sombre magique",
    labelEn: "Dark magic box",
    accentColor: "#a78bfa",
    bodyHinge: 88,
    body: svgWrap(`
      <defs>
        <linearGradient id="dm_body" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#3b1d6e"/>
          <stop offset="55%" stop-color="#1f1147"/>
          <stop offset="100%" stop-color="#050111"/>
        </linearGradient>
        <linearGradient id="dm_gloss" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="rgba(167,139,250,0.45)"/>
          <stop offset="55%" stop-color="rgba(167,139,250,0)"/>
        </linearGradient>
        <radialGradient id="dm_seal" cx="50%" cy="50%" r="60%">
          <stop offset="0%" stop-color="#fbbf24"/>
          <stop offset="60%" stop-color="#a78bfa"/>
          <stop offset="100%" stop-color="rgba(167,139,250,0)"/>
        </radialGradient>
      </defs>
      <ellipse cx="100" cy="186" rx="76" ry="9" fill="rgba(167,139,250,0.45)"/>
      <rect x="34" y="88" width="132" height="94" rx="6" fill="url(#dm_body)"/>
      <rect x="34" y="88" width="132" height="40" rx="6" fill="url(#dm_gloss)"/>
      <rect x="34" y="88" width="132" height="94" rx="6" fill="none" stroke="rgba(167,139,250,0.45)" stroke-width="1"/>
      <text x="100" y="118" font-family="serif" font-size="13" font-weight="900" fill="#a78bfa" text-anchor="middle" letter-spacing="2" opacity="0.85">⟁ ✦ ⟁</text>
      <circle cx="100" cy="148" r="22" fill="url(#dm_seal)" opacity="0.55"/>
      <text x="100" y="153" font-family="serif" font-size="14" font-weight="900" fill="#fbbf24" text-anchor="middle" letter-spacing="2">QR</text>
      <text x="100" y="174" font-family="serif" font-size="13" font-weight="900" fill="#a78bfa" text-anchor="middle" letter-spacing="2" opacity="0.85">⟁ ✦ ⟁</text>
      <path d="M40 95 Q100 84 160 95" stroke="#a78bfa" stroke-width="1.5" fill="none" opacity="0.6"/>
    `),
    lid: svgWrap(`
      <defs>
        <linearGradient id="dm_lid" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#5b21b6"/>
          <stop offset="55%" stop-color="#3b1d6e"/>
          <stop offset="100%" stop-color="#1a0a4a"/>
        </linearGradient>
        <linearGradient id="dm_lidshine" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="rgba(255,255,255,0.5)"/>
          <stop offset="55%" stop-color="rgba(255,255,255,0)"/>
        </linearGradient>
      </defs>
      <rect x="30" y="60" width="140" height="38" rx="4" fill="url(#dm_lid)"/>
      <rect x="30" y="60" width="140" height="14" fill="url(#dm_lidshine)"/>
      <rect x="30" y="60" width="140" height="6" fill="#7c3aed"/>
      <rect x="30" y="60" width="140" height="38" rx="4" fill="none" stroke="rgba(167,139,250,0.55)" stroke-width="1"/>
      <text x="100" y="86" font-family="serif" font-size="14" font-weight="900" fill="#fbbf24" text-anchor="middle" letter-spacing="3">✶ MYSTÈRE ✶</text>
    `),
  },

  // --- 5. Branded e-commerce carton ---------------------------------------
  branded_carton: {
    label: "Carton e-commerce QRISE",
    labelEn: "QRISE branded carton",
    accentColor: "#7c3aed",
    bodyHinge: 88,
    body: svgWrap(`
      <defs>
        <linearGradient id="bc_body" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#fde68a"/><stop offset="100%" stop-color="#a16207"/>
        </linearGradient>
      </defs>
      <ellipse cx="100" cy="186" rx="76" ry="8" fill="rgba(0,0,0,0.28)"/>
      <rect x="34" y="88" width="132" height="94" rx="3" fill="url(#bc_body)"/>
      <path d="M34 88 L166 88 L158 100 L42 100 Z" fill="#a16207" opacity="0.4"/>
      <rect x="34" y="124" width="132" height="14" fill="#7c3aed"/>
      <text x="100" y="135" font-family="sans-serif" font-size="11" font-weight="900" fill="#fff" text-anchor="middle" letter-spacing="6">QRISE</text>
      <line x1="46" y1="155" x2="74" y2="155" stroke="#451a03" stroke-width="1" opacity="0.6"/>
      <line x1="46" y1="165" x2="84" y2="165" stroke="#451a03" stroke-width="1" opacity="0.6"/>
    `),
    lid: svgWrap(`
      <defs>
        <linearGradient id="bc_lid" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#fde68a"/><stop offset="100%" stop-color="#b45309"/>
        </linearGradient>
      </defs>
      <rect x="32" y="62" width="136" height="32" rx="3" fill="url(#bc_lid)"/>
      <rect x="32" y="62" width="136" height="6" fill="#a16207" opacity="0.5"/>
      <rect x="32" y="76" width="136" height="6" fill="#7c3aed"/>
    `),
  },
};

export function getBoxStyleData(key) {
  return STYLES[key] || STYLES.gold_gift;
}

export function getBoxStyleBody(key) {
  return dataUri(getBoxStyleData(key).body);
}

export function getBoxStyleLid(key) {
  return dataUri(getBoxStyleData(key).lid);
}

export function getBoxStyleAccent(key) {
  return getBoxStyleData(key).accentColor;
}

export function getBoxStyleLabel(key, lang = "fr") {
  const s = getBoxStyleData(key);
  return lang === "fr" ? s.label : s.labelEn;
}

export const ALL_MYSTERY_STYLES = Object.keys(STYLES);
